import { ArrowUpRight, Clock, Users, Tag, Check } from "lucide-react";
import { SERVICES } from "@/lib/constants";

function ServiceCard({ s, large = false }) {
  return (
    <article
      data-testid={`service-card-${s.id}`}
      className={`group relative overflow-hidden rounded-2xl bg-white border border-black/5 shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col`}
    >
      <div className={`relative overflow-hidden ${large ? "h-[420px]" : "h-[260px]"}`}>
        <img
          src={s.image}
          alt={s.title}
          className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
        />
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm text-[#5C5654] text-[10px] tracking-[0.22em] uppercase font-bold px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3FB6B2]" />
            {s.overline}
          </span>
        </div>
      </div>

      <div className={`p-7 md:p-8 flex flex-col flex-1 ${large ? "lg:p-10" : ""}`}>
        <h3
          className={`font-display tracking-tight text-[#2A2726] ${
            large ? "text-3xl md:text-4xl font-medium" : "text-2xl font-medium"
          }`}
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          {s.title}
        </h3>
        <p className="mt-4 text-[#5C5654] leading-relaxed text-[15px]">
          {s.blurb}
        </p>

        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
          {s.features.map((f) => (
            <li
              key={f}
              className="flex items-start gap-2 text-sm text-[#2A2726]"
            >
              <Check
                size={16}
                className="mt-0.5 flex-shrink-0"
                style={{ color: "#3FB6B2" }}
              />
              {f}
            </li>
          ))}
        </ul>

        <div className="mt-8 pt-6 border-t border-black/5 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs uppercase tracking-[0.18em] text-[#5C5654]/80">
          <span className="inline-flex items-center gap-1.5">
            <Clock size={13} /> {s.duration}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Users size={13} /> {s.group}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Tag size={13} /> {s.price}
          </span>
        </div>

        <a
          href="#book"
          data-testid={`service-cta-${s.id}`}
          className="mt-7 inline-flex items-center justify-between w-full rounded-full px-5 py-3 text-sm font-medium text-white transition-all hover:-translate-y-0.5"
          style={{ backgroundColor: "#3FB6B2" }}
        >
          Enquire about this {s.overline.toLowerCase()}
          <ArrowUpRight size={16} />
        </a>
      </div>
    </article>
  );
}

export default function Services() {
  const [featured, ...rest] = SERVICES;
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#F9F8F6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="lg:col-span-5">
            <div className="overline mb-4">What we do</div>
            <h2
              className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2A2726] tracking-tight leading-[1.05]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Three ways onto
              <br />
              the rock.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-[#5C5654] text-lg leading-relaxed">
              From a first knot tied at the foot of a Lakeland crag, to a fifty-strong
              charity abseil, to a tailored 1:1 private coaching day — every session
              is delivered with the same care, qualifications and obsession with
              the mountains.
            </p>
          </div>
        </div>

        {/* Asymmetric bento layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          <div className="lg:col-span-7">
            <ServiceCard s={featured} large />
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 gap-6 lg:gap-8">
            {rest.map((s) => (
              <ServiceCard key={s.id} s={s} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
